import { useEffect, useId, useState, useContext } from 'react';
import { useGlobalHydrationActions } from '@jira/ssr__global-hydration-completeness/src/useIsGlobalHydrationComplete.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { WaitContext } from '@atlassian/react-loosely-lazy';

type Params = {
	// True if the suspense's fallback or children component has completed rendering
	isSubtreeRendered: boolean;
	name: string;
};

/**
 * We want to know if a component originated from the server, so would be eligible for hydration.
 */
export const componentIsServerRendered = (useIdValue: string): boolean =>
	useIdValue.startsWith(':R');

/**
 * This component is rendered at all suspense boundaries (placeholders), to be able to know when
 * hydration has completed globally - when every subtree has rendered.
 */
export const useHydrationSubtreeCompleter = ({
	isSubtreeRendered,
	name: unstableName,
}: Params): string => {
	const id = useId();
	const [name] = useState(unstableName);
	const { addPendingSubtree, completePendingSubtree } = useGlobalHydrationActions();

	const waitContext = useContext(WaitContext);

	useEffect(() => {
		if (waitContext?.currentValue() === 0) {
			// no effect if we are inside a LazyWait boundary
			return;
		}
		if (fg('jfp-magma-hydration-completion-fix-timing')) {
			if (!componentIsServerRendered(id)) {
				return;
			}
		}

		if (isSubtreeRendered) {
			completePendingSubtree(id);
		} else {
			addPendingSubtree(id, name);
		}
	}, [isSubtreeRendered, id, name, completePendingSubtree, addPendingSubtree, waitContext]);

	// Exists to ensure that we complete a subtree if it unmounts before `isSubtreeRendered` flips to true
	useEffect(() => {
		if (fg('jfp-magma-hydration-completion-fix-timing')) {
			if (!componentIsServerRendered(id)) {
				return;
			}
		}

		return () => {
			completePendingSubtree(id);
		};
	}, [id, completePendingSubtree]);

	// prevent dead code removal
	return id;
};
